/**
 * CustomFormElement model
 *
 * @class
 */
import Base from '@netx/core/lib/netx-model';

class CustomFormElement extends Base {
	/**
	 * @property {object}  defaults            - Model defaults
	 * @property {string}  defaults.type       - element type, (text, textarea, number, date, select, radios, checkboxes, multiselect)
	 * @property {string}  defaults.label      - display label
	 * @property {string}  defaults.value      - element options
	 * @property {string}  defaults.name       - nice name
	 * @property {string}  defaults.required   - required string
	 * @property {boolean} defaults.disabled   - if the form is read only
	 * @property {string}  defaults.userValue  - data, if any, associated with this element
	 * @property {string}  defaults.userValue  - checkbox data, if any, associated with this element
	 */
	get defaults() {
		return {
			type: '',
			label: '',
			value: '',
			name: '',
			required: '',
			disabled: false,
			userValue: '',
			userValues: [],
		};
	}

	/** @override */
	constructor(attrs, opts) {
		super(attrs, opts);

		let label = this.get('label');
		if (!label) return;

		const toSet = {};
		const value = this.get('value');

		let lang = i18n.language.slice(0, 2);
		if (label[lang] == null) {
			lang = 'en';
		}

		if (typeof label === 'object') {
			toSet.label = label[lang];

			// key should be mandatory, but it's left off sections in the wild
			if (!label.key && this.get('type') === 'section') {
				label.key = 'section' + this.cid;
			}
		} else if (typeof label === 'string') {
			// just incase
			toSet.label = label;
		}

		toSet.name = label.key ? label.key : this.makeName(toSet.label);

		if (_.isArray(value)) {
			_.each(value, (opt, idx, all) => {
				if (typeof opt === 'object') {
					all[idx] = {
						key: opt.key,
						value: opt[lang],
						disabled: opt.disabled,
					};
				} else {
					all[idx] = {
						key: this.makeName(opt),
						value: opt,
						disabled: opt.disabled,
					};
				}
			});
		}

		if (this.collection.readOnly) {
			toSet.disabled = true;
		}

		this.set(toSet);
	}
	/** @method */
	makeName(elementLabel) {
		return this.constructor.makeName(elementLabel);
	}
	/**
	 * @method
	 * @return parsed element label
	 */
	getDisplayLabel() {
		const label = this.get('label');
		// remove :
		return label.replace(/:\s*$/, '');
	}
	/**
	 * @method
	 * @return parsed element value
	 */
	getDisplayValue(value) {
		switch (this.get('type')) {
			case 'checkboxes':
			case 'multiselect':
				return value.replace(/","/g, ', ');
		}
		return value;
	}

	/**
	 * @method
	 * @static
	 * @param {string} elementLabel Element label
	 * @return {string} Name
	 */
	static makeName(elementLabel) {
		if (elementLabel) {
			return elementLabel
				.toLowerCase() // make everything lowercase
				.replace(/[-\W]+/g, '-') // convert pattersn of multiple dashes or non-word characters -> a single dash
				.replace(/^-/, '') // strip any dash from front of slug
				.replace(/-$/, '');
		} else {
			console.warn('form element name error');
			return '';
		}
	}

	/** @type {string} */
	static get __name__() {
		return 'CustomFormElement';
	}
}

export default CustomFormElement;
